import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Layers, Lock, Pencil, Settings } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { CatalogoDePermisos, GrupoDeMenu, ItemDeMenu, Menu } from '@/shared/api/tipos';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Interruptor } from '@/shared/ui/Interruptor';
import { PanelLateral, CampoDelPanel, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { Icono } from '@/shared/ui/Icono';
import { ICONOS_DISPONIBLES } from '@/shared/ui/Icono';

const esquema = z.object({
  etiqueta: z.string().min(1, 'El texto es obligatorio').max(80),
  icono: z.string().min(1, 'Elija un icono').max(50),
});

type Formulario = z.infer<typeof esquema>;

type Edicion =
  | { tipo: 'grupo'; id: number; texto: string; icono: string }
  | { tipo: 'item'; id: number; texto: string; icono: string };

export function PaginaMenu() {
  const clienteDeConsultas = useQueryClient();
  const [editando, setEditando] = useState<Edicion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const menu = useQuery({
    queryKey: ['menu', 'administracion'],
    queryFn: () => api.get<Menu>('/menu/administracion'),
  });

  const catalogo = useQuery({
    queryKey: ['permisos'],
    queryFn: () => api.get<CatalogoDePermisos>('/permisos'),
    staleTime: Infinity,
  });

  const nombreDePermiso = useMemo(() => {
    const porSlug = new Map<string, string>();
    for (const permiso of Object.values(catalogo.data ?? {}).flat()) {
      porSlug.set(permiso.slug, permiso.nombre);
    }
    return porSlug;
  }, [catalogo.data]);

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['menu'] });
  }

  const cambiarVisibilidad = useMutation({
    mutationFn: ({ tipo, id, visible }: { tipo: 'grupos' | 'items'; id: number; visible: boolean }) =>
      api.put<void>(`/menu/${tipo}/${id}`, { visible }),
    onSuccess: refrescar,
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar la visibilidad';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const sueltos = [...(menu.data?.superiores ?? []), ...(menu.data?.inferiores ?? [])];

  return (
    <>
      <EncabezadoDePagina
        icono={Settings}
        color="azul"
        titulo="Configuración del menú"
        subtitulo="Organice, renombre y oculte las entradas de la barra lateral"
      />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <p className="mb-5 flex items-start gap-2.5 rounded-lg border border-armazon-300 bg-white px-4 py-3 text-xs font-medium uppercase tracking-wide text-armazon-700">
        <Lock className="mt-px size-4 shrink-0 text-armazon-600" aria-hidden />
        La ruta y el permiso de cada entrada no se pueden cambiar: apuntan a pantallas y
        reglas del sistema. Aquí se organiza y se renombra el menú.
      </p>

      {menu.isPending ? (
        <Cargando titulo="Cargando el menú" detalle="Trayendo grupos y entradas" alto="h-64" />
      ) : (
        <div className="grid gap-4">
          {sueltos.length > 0 && (
            <section className="rounded-2xl border border-armazon-100 bg-white shadow-sm">
              <header className="flex items-center gap-3 border-b border-armazon-100 px-5 py-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-armazon-100 text-armazon-600">
                  <Layers className="size-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold uppercase tracking-wide text-armazon-800">
                    Entradas sueltas
                  </p>
                  <p className="text-[11px] uppercase text-armazon-600">
                    Fuera de los grupos, arriba y abajo de la barra
                  </p>
                </div>
              </header>

              <ul className="divide-y divide-armazon-100">
                {sueltos.map((item) => (
                  <Entrada
                    key={item.id}
                    item={item}
                    ocupado={cambiarVisibilidad.isPending}
                    nombreDePermiso={nombreDePermiso}
                    alAlternar={() =>
                      cambiarVisibilidad.mutate({
                        tipo: 'items',
                        id: item.id,
                        visible: !item.visible,
                      })
                    }
                    alEditar={() =>
                      setEditando({
                        tipo: 'item',
                        id: item.id,
                        texto: item.etiqueta,
                        icono: item.icono,
                      })
                    }
                  />
                ))}
              </ul>
            </section>
          )}

          {menu.data?.grupos.map((grupo) => (
            <Grupo
              key={grupo.id}
              grupo={grupo}
              ocupado={cambiarVisibilidad.isPending}
              nombreDePermiso={nombreDePermiso}
              alAlternarGrupo={() =>
                cambiarVisibilidad.mutate({
                  tipo: 'grupos',
                  id: grupo.id,
                  visible: !grupo.visible,
                })
              }
              alAlternarItem={(item) =>
                cambiarVisibilidad.mutate({
                  tipo: 'items',
                  id: item.id,
                  visible: !item.visible,
                })
              }
              alEditarGrupo={() =>
                setEditando({
                  tipo: 'grupo',
                  id: grupo.id,
                  texto: grupo.titulo,
                  icono: grupo.icono,
                })
              }
              alEditarItem={(item) =>
                setEditando({
                  tipo: 'item',
                  id: item.id,
                  texto: item.etiqueta,
                  icono: item.icono,
                })
              }
            />
          ))}
        </div>
      )}

      {editando && (
        <FormularioDeMenu
          key={`${editando.tipo}-${editando.id}`}
          edicion={editando}
          alCerrar={() => setEditando(null)}
          alGuardar={refrescar}
        />
      )}
    </>
  );
}

function Grupo({
  grupo,
  ocupado,
  nombreDePermiso,
  alAlternarGrupo,
  alAlternarItem,
  alEditarGrupo,
  alEditarItem,
}: {
  grupo: GrupoDeMenu;
  ocupado: boolean;
  nombreDePermiso: Map<string, string>;
  alAlternarGrupo: () => void;
  alAlternarItem: (item: ItemDeMenu) => void;
  alEditarGrupo: () => void;
  alEditarItem: (item: ItemDeMenu) => void;
}) {
  return (
    <section
      className={[
        'rounded-2xl border bg-white shadow-sm transition-opacity',
        grupo.visible ? 'border-armazon-100' : 'border-dashed border-armazon-300 opacity-60',
      ].join(' ')}
    >
      <header className="flex items-center gap-3 border-b border-armazon-100 px-5 py-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-marca-50 text-marca-700">
          <Icono nombre={grupo.icono} className="size-4" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold uppercase tracking-wide text-armazon-800">
            {grupo.titulo}
          </p>
          <p className="text-[11px] uppercase text-armazon-600">
            {grupo.items.length} {grupo.items.length === 1 ? 'entrada' : 'entradas'} · {grupo.clave}
          </p>
        </div>

        <Interruptor
          activo={grupo.visible}
          deshabilitado={ocupado}
          etiqueta={`${grupo.visible ? 'Ocultar' : 'Mostrar'} el grupo ${grupo.titulo}`}
          alCambiar={alAlternarGrupo}
        />

        <button
          type="button"
          onClick={alEditarGrupo}
          title="Editar"
          aria-label={`Editar el grupo ${grupo.titulo}`}
          className="rounded-lg p-1.5 text-armazon-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil className="size-[15px]" aria-hidden />
        </button>
      </header>

      <ul className="divide-y divide-armazon-100">
        {grupo.items.map((item) => (
          <Entrada
            key={item.id}
            item={item}
            ocupado={ocupado}
            nombreDePermiso={nombreDePermiso}
            alAlternar={() => alAlternarItem(item)}
            alEditar={() => alEditarItem(item)}
          />
        ))}
      </ul>
    </section>
  );
}

function Entrada({
  item,
  ocupado,
  nombreDePermiso,
  alAlternar,
  alEditar,
}: {
  item: ItemDeMenu;
  ocupado: boolean;
  nombreDePermiso: Map<string, string>;
  alAlternar: () => void;
  alEditar: () => void;
}) {
  return (
    <li
      className={[
        'flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-armazon-50/60',
        item.visible ? '' : 'opacity-55',
      ].join(' ')}
    >
      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-armazon-100 text-armazon-600">
        <Icono nombre={item.icono} className="size-3.5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-armazon-800">{item.etiqueta}</p>
        <p className="truncate font-mono text-[11px] text-armazon-500">{item.href}</p>
      </div>

      {item.soloAdmin ? (
        <span className="hidden shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-300 sm:inline">
          Solo administrador
        </span>
      ) : item.permiso ? (
        <span
          title={item.permiso}
          className="hidden shrink-0 rounded bg-armazon-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-armazon-700 sm:inline"
        >
          {nombreDePermiso.get(item.permiso) ?? item.permiso}
        </span>
      ) : (
        <span className="hidden shrink-0 text-[10px] font-medium uppercase tracking-wide text-armazon-500 sm:inline">
          Todos
        </span>
      )}

      <span className="shrink-0 text-armazon-500" title={item.visible ? 'Visible' : 'Oculta'}>
        {item.visible ? (
          <Eye className="size-3.5" aria-hidden />
        ) : (
          <EyeOff className="size-3.5" aria-hidden />
        )}
      </span>

      <Interruptor
        activo={item.visible}
        deshabilitado={ocupado}
        etiqueta={`${item.visible ? 'Ocultar' : 'Mostrar'} ${item.etiqueta}`}
        alCambiar={alAlternar}
      />

      <button
        type="button"
        onClick={alEditar}
        title="Editar"
        aria-label={`Editar ${item.etiqueta}`}
        className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
      >
        <Pencil className="size-[15px]" aria-hidden />
      </button>
    </li>
  );
}

function FormularioDeMenu({
  edicion,
  alCerrar,
  alGuardar,
}: {
  edicion: Edicion;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { etiqueta: edicion.texto, icono: edicion.icono },
  });

  const iconoElegido = watch('icono');
  const esGrupo = edicion.tipo === 'grupo';

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {

      const cuerpo = esGrupo
        ? { titulo: datos.etiqueta, icono: datos.icono }
        : { etiqueta: datos.etiqueta, icono: datos.icono };

      await api.put<void>(`/menu/${esGrupo ? 'grupos' : 'items'}/${edicion.id}`, cuerpo);
      alGuardar();
      alCerrar();
      notificar.exito('Entrada de menú actualizada');
    } catch (causa) {
      setErrorGeneral(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar');
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar',
      );
    }
  });

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      titulo={esGrupo ? 'Editar grupo' : 'Editar entrada'}
      descripcion={esGrupo ? 'Título e icono del grupo' : 'Texto e icono de la entrada'}
      icono={esGrupo ? Layers : Pencil}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      <CampoDelPanel
        etiqueta={esGrupo ? 'Título' : 'Texto'}
        requerido
        error={errors.etiqueta?.message}
      >
        <input {...register('etiqueta')} className={claseEntradaPanel} autoFocus />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Icono"
        requerido
        error={errors.icono?.message}
        ayuda="Solo los iconos que la barra lateral sabe dibujar"
      >

        <input type="hidden" {...register('icono')} />
        <div className="grid max-h-56 grid-cols-8 gap-1.5 overflow-y-auto rounded-lg border border-armazon-200 p-2">
          {ICONOS_DISPONIBLES.map((nombre) => (
            <button
              key={nombre}
              type="button"
              onClick={() => setValue('icono', nombre, { shouldValidate: true })}
              title={nombre}
              aria-label={nombre}
              aria-pressed={iconoElegido === nombre}
              className={[
                'grid aspect-square place-items-center rounded-md transition-colors',
                iconoElegido === nombre
                  ? 'bg-marca-600 text-white'
                  : 'text-armazon-500 hover:bg-armazon-100 hover:text-armazon-800',
              ].join(' ')}
            >
              <Icono nombre={nombre} className="size-4" />
            </button>
          ))}
        </div>
      </CampoDelPanel>
    </PanelLateral>
  );
}
